import { createSelector } from "@reduxjs/toolkit";
import type { RootState } from "@store/index";
import type { WorkspaceResourceView } from "@/types/workspace";
import {
  selectAllWorkspaceReadings,
  selectReadingsLoading,
} from "@features/workspace-readings/state/workspaceReadingsSlice";
import {
  selectAllWorkspaceCanvases,
  selectWorkspaceCanvasesLoading,
} from "@features/workspace-canvases/state/workspaceCanvasesSlice";
import {
  selectAllWorkspacePapers,
  selectWorkspacePapersLoading,
} from "@features/workspace-papers/state/workspacePapersSlice";

export const selectUnifiedResources = createSelector(
  [
    selectAllWorkspaceReadings,
    selectAllWorkspaceCanvases,
    selectAllWorkspacePapers,
  ],
  (readings, canvases, papers): WorkspaceResourceView[] => {
    const readingRows: WorkspaceResourceView[] = readings.map((r) => ({
      id: r.reading_id,
      type: "reading",
      name: r.reading_name,
      description: r.description,
      assignedByUsername: r.assigned_by_username,
      createdAt: r.created_at,
      dueDate: r.due_date,
      required: r.required,
      assignmentKind: r.assignment_kind,
      myStatus: r.my_status,
      myPoints: r.my_points,
      myMaxPoints: r.my_max_points,
      mySubmittedAt: r.my_submitted_at,
      assignedCount: r.assigned_user_count,
      submittedCount: r.completed_count,
      inProgressCount: r.in_progress_count,
      notStartedCount: r.not_started_count,
    }));
    const canvasRows: WorkspaceResourceView[] = canvases.map((c) => ({
      id: c.canvas_id,
      type: "canvas",
      name: c.canvas_name,
      description: c.description,
      assignedByUsername: c.assigned_by_username,
      createdAt: c.created_at,
      dueDate: c.due_date,
      required: c.required,
      assignmentKind: c.assignment_kind,
      myStatus: c.my_status,
      myPoints: c.my_points,
      myMaxPoints: c.my_max_points,
      mySubmittedAt: c.my_submitted_at,
      assignedCount: c.assigned_user_count,
      submittedCount: c.completed_count,
      inProgressCount: c.in_progress_count,
      notStartedCount: c.not_started_count,
    }));
    const paperRows: WorkspaceResourceView[] = papers.map((p) => ({
      id: p.paper_id,
      type: "paper",
      name: p.paper_name,
      description: p.description,
      assignedByUsername: p.assigned_by_username,
      createdAt: p.created_at,
      dueDate: p.due_date,
      required: p.required,
      assignmentKind: p.assignment_kind,
      myStatus: p.my_status,
      myPoints: p.my_points,
      myMaxPoints: p.my_max_points,
      mySubmittedAt: p.my_submitted_at,
      assignedCount: p.assigned_user_count,
      submittedCount: p.completed_count,
      inProgressCount: p.in_progress_count,
      notStartedCount: p.not_started_count,
    }));
    return [...readingRows, ...canvasRows, ...paperRows];
  }
);

export const selectUnifiedResourcesLoading = (state: RootState): boolean =>
  selectReadingsLoading(state) ||
  selectWorkspaceCanvasesLoading(state) ||
  selectWorkspacePapersLoading(state);
